import { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Scale, Lock, ArrowRight, AlertCircle, UserRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { SeoHead, SEO_KEYWORDS_PRIMARY } from '../components/SeoHead';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Force no-cache behavior for login view on mobile browsers.
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Cache-Control';
    meta.content = 'no-store, no-cache, must-revalidate, max-age=0';
    document.head.appendChild(meta);
    return () => {
      if (meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      const params = new URLSearchParams(location.search);
      const next = params.get('next');
      const safeNext = next && next.startsWith('/') ? next : '/dashboard';
      navigate(safeNext, { replace: true });
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#0A192F' }}>
      <SeoHead
        title="Accesso Area Riservata | Lead Legale – consulenza legale e gestione lead"
        description="Accedi alla dashboard Lead Legale per la gestione dei lead in ambito infortunistica stradale, risarcimento danni e consulenza legale riservata ai professionisti."
        keywords={SEO_KEYWORDS_PRIMARY}
        path="/login"
      />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Scale className="w-10 h-10" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <span className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>LeadLegale</span>
          </Link>
        </div>

        {/* Login Card */}
        <div className="glass-card p-8 animate-fade-in">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
              Area Riservata
            </h1>
            <p style={{ color: '#A0ABC0' }}>Accedi alla tua dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="flex items-center gap-2 mb-2" style={{ color: '#A0ABC0' }}>
                <UserRound className="w-4 h-4" strokeWidth={1.5} />
                Email
              </Label>
              <Input
                type="email"
                placeholder="nome@studiolegale.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-luxury w-full"
                data-testid="login-email-input"
                style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2" style={{ color: '#A0ABC0' }}>
                <Lock className="w-4 h-4" strokeWidth={1.5} />
                Password
              </Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-luxury w-full"
                data-testid="login-password-input"
                style={{ backgroundColor: '#112240', borderColor: 'rgba(255,255,255,0.1)', color: '#F8F9FA' }}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-md" style={{ backgroundColor: 'rgba(255, 76, 76, 0.1)', border: '1px solid rgba(255, 76, 76, 0.3)' }}>
                <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#FF4C4C' }} />
                <p className="text-sm break-words leading-snug" style={{ color: '#FF4C4C' }}>{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
              style={{ backgroundColor: '#D4AF37', color: '#0A192F' }}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#0A192F', borderTopColor: 'transparent' }}></div>
                  Accesso in corso...
                </>
              ) : (
                <>
                  Accedi
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>
          <div className="mt-8 text-center">
            <Link to="/" className="text-sm transition-colors hover:text-[#D4AF37]" style={{ color: '#A0ABC0' }}>
              ← Torna alla Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
